import { useState, useEffect } from 'react';
import { resultsAPI, adminAPI } from '@/services/api';
import { getGradeColor } from '@/utils/helpers';
import toast from 'react-hot-toast';

const StaffResults = () => {
  const [selectedClass, setSelectedClass] = useState('SS1');
  const [selectedSubject, setSelectedSubject] = useState('Mathematics');
  const [term, setTerm] = useState('1st');
  const [session, setSession] = useState('2025/2026');
  const [students, setStudents] = useState([]);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const subjects = ['Mathematics', 'English Language', 'Islamic Studies', 'Physics', 'Chemistry', 'Biology', 'Geography', 'Computer Science'];
  const classes = ['SS1', 'SS2', 'SS3'];

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const res = await adminAPI.getStudents({ class: selectedClass });
        const data = res.data.data || [];
        setStudents(data);
        // Initialize empty results
        const init = {};
        data.forEach((s) => {
          init[s.id] = { caScore: '', examScore: '' };
        });
        setResults(init);
      } catch (err) {
        console.error('Students fetch error:', err);
        toast.error('Failed to load students');
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, [selectedClass]);

  const handleResultChange = (studentId, field, value) => {
    setResults((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], [field]: value },
    }));
  };

  const calculateTotal = (ca, exam) => {
    const caNum = parseFloat(ca) || 0;
    const examNum = parseFloat(exam) || 0;
    return caNum + examNum;
  };

  const calculateGrade = (total) => {
    if (total >= 70) return 'A';
    if (total >= 60) return 'B';
    if (total >= 50) return 'C';
    if (total >= 45) return 'D';
    if (total >= 40) return 'E';
    return 'F';
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        class: selectedClass,
        subject: selectedSubject,
        term,
        session,
        results: students.map((s) => ({
          student_id: s.id,
          ca_score: parseFloat(results[s.id]?.caScore) || 0,
          exam_score: parseFloat(results[s.id]?.examScore) || 0,
        })),
      };
      await resultsAPI.uploadResults(payload);
      toast.success('Results uploaded successfully!');
    } catch (err) {
      console.error('Results upload error:', err);
      toast.error(err.response?.data?.message || 'Failed to upload results');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-black text-slate-800">Upload Results</h2>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Class</label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all text-slate-800 font-medium"
          >
            {classes.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Subject</label>
          <select
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all text-slate-800 font-medium"
          >
            {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Term</label>
          <select
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all text-slate-800 font-medium"
          >
            <option value="1st">1st Term</option>
            <option value="2nd">2nd Term</option>
            <option value="3rd">3rd Term</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Session</label>
          <select
            value={session}
            onChange={(e) => setSession(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all text-slate-800 font-medium"
          >
            <option value="2025/2026">2025/2026</option>
            <option value="2024/2025">2024/2025</option>
          </select>
        </div>
      </div>

      {/* Results Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <i className="fas fa-table text-emerald-500" /> {selectedSubject} — {selectedClass} ({term} Term, {session})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Student</th>
                <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Admission No</th>
                <th className="text-center px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">CA (40)</th>
                <th className="text-center px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Exam (60)</th>
                <th className="text-center px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Total</th>
                <th className="text-center px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Grade</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {students.length > 0 ? students.map((student) => {
                const ca = results[student.id]?.caScore || '';
                const exam = results[student.id]?.examScore || '';
                const total = calculateTotal(ca, exam);
                const grade = calculateGrade(total);
                return (
                  <tr key={student.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 font-semibold text-slate-800 text-sm">{student.full_name || '—'}</td>
                    <td className="px-6 py-4 text-sm text-slate-600 font-mono">{student.admission_number || '—'}</td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="number"
                        min="0"
                        max="40"
                        value={ca}
                        onChange={(e) => handleResultChange(student.id, 'caScore', e.target.value)}
                        className="w-20 px-3 py-2 rounded-lg border-2 border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-center text-sm font-semibold"
                      />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="number"
                        min="0"
                        max="60"
                        value={exam}
                        onChange={(e) => handleResultChange(student.id, 'examScore', e.target.value)}
                        className="w-20 px-3 py-2 rounded-lg border-2 border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-center text-sm font-semibold"
                      />
                    </td>
                    <td className="px-6 py-4 text-center font-bold text-slate-800 text-sm">{total || '—'}</td>
                    <td className="px-6 py-4 text-center">
                      {grade !== '—' ? (
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${getGradeColor(grade)}`}>
                          {grade}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-sm">—</span>
                      )}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-slate-400">
                    <i className="fas fa-inbox text-3xl mb-3 block" />
                    <p className="text-sm">No students found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="px-8 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save" /> Upload Results
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default StaffResults;

